import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Toolbar,
  AppBar,
  Divider,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Stack,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  FitScreen,
  RotateRight,
  Contrast,
  Brightness6,
  Download,
  GridOn,
  ContentCopy,
} from '@mui/icons-material';

interface DocumentViewerProps {
  documentId: string;
  imageUrl?: string;
  imageData?: ImageData;
  file?: File;
  onImageReady?: (imageData: ImageData) => void;
  extractedRegions?: Array<{
    bbox: { x0: number; y0: number; x1: number; y1: number };
    text?: string;
    confidence?: number;
    type?: string;
  }>;
  selectedRegion?: number;
  onRegionClick?: (index: number) => void;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentId,
  imageUrl,
  imageData: initialImageData,
  file,
  onImageReady,
  extractedRegions = [],
  selectedRegion,
  onRegionClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageData, setImageData] = useState<ImageData | null>(initialImageData || null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [showGrid, setShowGrid] = useState(false);
  const [showRegions, setShowRegions] = useState(true);
  const [fitMode, setFitMode] = useState<'width' | 'height' | 'none'>('width');
  const imageReadyCalledRef = useRef(false);

  // Reset flag when document changes
  useEffect(() => {
    imageReadyCalledRef.current = false;
  }, [documentId]);

  // Load image from various sources
  useEffect(() => {
    const loadImage = async () => {
      setLoading(true);
      try {
        let imgData: ImageData | null = null;

        if (initialImageData) {
          imgData = initialImageData;
        } else if (file) {
          const bitmap = await createImageBitmap(file);
          const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
          const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
          ctx.drawImage(bitmap, 0, 0);
          imgData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
        } else if (imageUrl) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageUrl;
          });
          const canvas = new OffscreenCanvas(img.width, img.height);
          const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
          ctx.drawImage(img, 0, 0);
          imgData = ctx.getImageData(0, 0, img.width, img.height);
        }

        if (imgData) {
          setImageData(imgData);
          // Only call onImageReady once per document
          if (onImageReady && !imageReadyCalledRef.current) {
            imageReadyCalledRef.current = true;
            onImageReady(imgData);
          }
        }
      } catch (error) {
        console.error('Failed to load image:', error);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [imageUrl, file, initialImageData, onImageReady]);

  // Render image to canvas
  useEffect(() => {
    if (!canvasRef.current || !imageData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;

    // Apply transformations
    const processedImage = applyImageFilters(imageData, brightness, contrast);

    // Set canvas size
    canvas.width = processedImage.width;
    canvas.height = processedImage.height;

    // Clear and draw
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.putImageData(processedImage, 0, 0);

    // Draw grid overlay
    if (showGrid) {
      drawGrid(ctx, canvas.width, canvas.height);
    }

    // Draw extraction regions
    if (showRegions && extractedRegions.length > 0) {
      drawRegions(ctx, extractedRegions, selectedRegion);
    }
  }, [imageData, brightness, contrast, showGrid, showRegions, extractedRegions, selectedRegion]);

  const applyImageFilters = (
    img: ImageData,
    brightness: number,
    contrast: number
  ): ImageData => {
    const output = new ImageData(
      new Uint8ClampedArray(img.data),
      img.width,
      img.height
    );

    const factor = (contrast / 100) * 2;
    const intercept = 128 * (1 - factor);
    const brightnessFactor = brightness / 100;

    for (let i = 0; i < output.data.length; i += 4) {
      for (let j = 0; j < 3; j++) {
        let value = output.data[i + j];
        // Apply contrast
        value = value * factor + intercept;
        // Apply brightness
        value = value * brightnessFactor;
        // Clamp
        output.data[i + j] = Math.max(0, Math.min(255, value));
      }
    }

    return output;
  };

  const drawGrid = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.strokeStyle = 'rgba(100, 100, 100, 0.3)';
    ctx.lineWidth = 1;

    const gridSize = 50;
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  const drawRegions = (
    ctx: CanvasRenderingContext2D,
    regions: DocumentViewerProps['extractedRegions'],
    selected?: number
  ) => {
    if (!regions || regions.length === 0) return;
    regions.forEach((region, index) => {
      const { x0, y0, x1, y1 } = region.bbox;
      const isSelected = index === selected;

      // Draw rectangle
      ctx.strokeStyle = isSelected
        ? 'rgba(255, 0, 0, 0.8)'
        : region.confidence && region.confidence > 0.8
        ? 'rgba(0, 255, 0, 0.6)'
        : 'rgba(255, 255, 0, 0.6)';
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);

      // Draw label
      if (region.type || region.confidence) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(x0, y0 - 20, 100, 20);
        ctx.fillStyle = 'white';
        ctx.font = '12px Arial';
        ctx.fillText(
          `${region.type || 'text'} ${
            region.confidence ? `(${(region.confidence * 100).toFixed(0)}%)` : ''
          }`,
          x0 + 2,
          y0 - 5
        );
      }
    });
  };

  const handleZoomIn = () => setZoom(Math.min(zoom * 1.2, 5));
  const handleZoomOut = () => setZoom(Math.max(zoom * 0.8, 0.1));
  const handleFitScreen = () => {
    setFitMode('width');
    setZoom(1);
  };

  const handleRotate = () => {
    setRotation((rotation + 90) % 360);
    // TODO: Implement actual rotation
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;
    
    canvasRef.current.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `document-${documentId}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    });
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || !onRegionClick) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / zoom;
    const y = (event.clientY - rect.top) / zoom;

    // Find clicked region
    const clickedIndex = extractedRegions.findIndex((region) => {
      const { x0, y0, x1, y1 } = region.bbox;
      return x >= x0 && x <= x1 && y >= y0 && y <= y1;
    });

    if (clickedIndex >= 0) {
      onRegionClick(clickedIndex);
    }
  };

  return (
    <Paper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ flexGrow: 0, mr: 2 }}>
            Document Viewer
          </Typography>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
          </Tooltip>
          
          <Typography variant="body2" sx={{ mx: 1, minWidth: 50, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </Typography>
          
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Fit to Screen">
            <IconButton onClick={handleFitScreen} size="small">
              <FitScreen />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <Tooltip title="Rotate">
            <IconButton onClick={handleRotate} size="small">
              <RotateRight />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mx: 1 }}>
            <Brightness6 fontSize="small" />
            <Slider
              value={brightness}
              onChange={(_, value) => setBrightness(value as number)}
              min={0}
              max={200}
              sx={{ width: 100 }}
              size="small"
            />
          </Stack>
          
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mx: 1 }}>
            <Contrast fontSize="small" />
            <Slider
              value={contrast}
              onChange={(_, value) => setContrast(value as number)}
              min={0}
              max={200}
              sx={{ width: 100 }}
              size="small"
            />
          </Stack>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <ToggleButtonGroup size="small" sx={{ mr: 1 }}>
            <ToggleButton
              value="grid"
              selected={showGrid}
              onChange={() => setShowGrid(!showGrid)}
            >
              <Tooltip title="Toggle Grid">
                <GridOn fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value="regions"
              selected={showRegions}
              onChange={() => setShowRegions(!showRegions)}
            >
              <Tooltip title="Toggle Regions">
                <ContentCopy fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Tooltip title="Download">
            <IconButton onClick={handleDownload} size="small">
              <Download />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          position: 'relative',
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : imageData ? (
          <canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg)`,
              transformOrigin: 'center',
              cursor: onRegionClick ? 'pointer' : 'default',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        ) : (
          <Typography color="text.secondary">No document loaded</Typography>
        )}
      </Box>
    </Paper>
  );
};